<div class="ac-dialog">
  <!-- Header -->
  <div class="ac-header">
    <div class="ac-header-left">
      <mat-icon class="ac-header-icon">person_outline</mat-icon>
      <span class="ac-header-title">{{ 'customers.add_contact.title' | translate }}</span>
    </div>
    <button class="ac-close-btn" (click)="cancel()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Body -->
  <div class="ac-body" [formGroup]="form">
    <!-- Contact Information -->
    <div class="ac-section">
      <p class="ac-section-title">{{ 'customers.add_contact.section_title' | translate }}</p>

      <div class="ac-row-2col">
        <div class="ac-field">
          <label class="ac-label">{{ 'common.first_name' | translate }}</label>
          <input class="ac-input" type="text" formControlName="contact_fname" [placeholder]="'customers.add_contact.first_name_placeholder' | translate">
          <span class="ac-error" *ngIf="form.get('contact_fname')?.touched && form.get('contact_fname')?.errors">
            {{ "common.required_field" | translate }}
          </span>
        </div>
        <div class="ac-field">
          <label class="ac-label">{{ 'common.last_name' | translate }}</label>
          <input class="ac-input" type="text" formControlName="contact_lname" [placeholder]="'customers.add_contact.last_name_placeholder' | translate">
        </div>
      </div>

      <div class="ac-field">
        <label class="ac-label">{{ 'common.email' | translate }}</label>
        <input class="ac-input" type="text" formControlName="email" [placeholder]="'customers.add_contact.email_placeholder' | translate">
        <span class="ac-error" *ngIf="form.get('email')?.touched && form.get('email')?.errors?.['required']">
          {{ "common.required_field" | translate }}
        </span>
        <span class="ac-error" *ngIf="form.get('email')?.touched && !form.get('email')?.errors?.['required'] && form.get('email')?.errors?.['invalidEmail']">
          {{ 'customers.add_contact.invalid_email' | translate }}
        </span>
      </div>

      <div class="ac-field">
        <label class="ac-label">{{ 'common.phone' | translate }} <span class="ac-optional">({{ 'common.optional' | translate }})</span></label>
        <input class="ac-input" type="text" formControlName="phone" [placeholder]="'customers.add_contact.phone_placeholder' | translate">
        <span class="ac-error" *ngIf="form.get('phone')?.touched && form.get('phone')?.errors?.['numericOnly']">
          {{ "validation.numeric_only" | translate }}
        </span>
      </div>

      <div class="ac-field">
        <label class="ac-label">{{ 'customers.add_contact.job_position' | translate }} <span class="ac-optional">({{ 'common.optional' | translate }})</span></label>
        <input class="ac-input" type="text" formControlName="signature" [placeholder]="'customers.add_contact.job_position_placeholder' | translate">
      </div>

      <div class="ac-field">
        <label class="ac-label">{{ "settings.language" | translate }}</label>
        <select class="ac-select" formControlName="lang">
          <option selected disabled value="">{{ "settings.language" | translate }}</option>
          <option value="it_IT">{{ "language.italian" | translate }}</option>
          <option value="en_US">{{ "language.english" | translate }}</option>
          <option value="fr_FR">{{ "language.french" | translate }}</option>
        </select>
        <span class="ac-error" *ngIf="form.get('lang')?.touched && form.get('lang')?.errors">
          {{ "common.required_field" | translate }}
        </span>
      </div>
    </div>

    <!-- "Main contact" toggle: marks this contact as is_user=true (primary person for this location).
         The control is disabled when the contact is already is_user=true — portal access cannot be revoked here.
         Hidden entirely when traditional contacts are off (WB-7369): no is_user main-contact selection. -->
    <div class="ac-toggle-card" *ngIf="featureFlagService.traditionalContactsEnabled">
      <div class="ac-toggle-text">
        <span class="ac-toggle-title">{{ 'customers.add_contact.is_main_title' | translate }}</span>
        <span class="ac-toggle-subtitle">{{ 'customers.add_contact.is_main_subtitle' | translate }}</span>
      </div>
      <mat-slide-toggle formControlName="is_user"></mat-slide-toggle>
    </div>

    <!-- "Invite to B2B portal" toggle: separate from the main-contact toggle above.
         Hidden when the contact was already a main contact (initialIsUserState=true) because portal
         access is already granted — showing it again would be misleading. Also hidden when
         traditional contacts are off (WB-7369), since both toggles share the same is_user control. -->
    <div class="ac-toggle-row" *ngIf="featureFlagService.traditionalContactsEnabled && initialIsUserState === false">
      <mat-slide-toggle formControlName="is_user"></mat-slide-toggle>
      <span class="ac-toggle-row-label">{{ "companies.invite_to_b2b_portal" | translate }}</span>
    </div>

    <!-- Receive Emails -->
    <div class="ac-toggle-row">
      <mat-slide-toggle formControlName="receive_emails"></mat-slide-toggle>
      <span class="ac-toggle-row-label">{{ "companies.receive_emails" | translate }}</span>
    </div>

    <!-- Location selector (shown only in the Contacts-tab creation flow) -->
    <div class="ac-field" *ngIf="data.showLocationSelector && data.availableLocations?.length">
      <label class="ac-label">{{ 'common.location' | translate }}</label>
      <select class="ac-select" [value]="selectedLocationId" (change)="onLocationChange($event)">
        <option *ngFor="let loc of data.availableLocations" [value]="loc.id">
          {{ loc.name }}{{ loc.is_main ? (' ' + ('customers.add_contact.location_main_suffix' | translate)) : '' }}
        </option>
      </select>
    </div>
  </div>

  <!-- Footer -->
  <div class="ac-footer">
    <button class="ac-btn-cancel" (click)="cancel()">{{ 'common.CANCEL' | translate }}</button>
    <div class="ac-footer-actions">
      <!-- Edit/delete actions are gated on the company edit permission; a user
           without it opens this dialog read-only (Cancel only). Delete shows in
           edit mode only — the parent confirms and performs the deletion. -->
      <ng-container *appHasPermission="{ module: 'company', action: 'edit' }">
        <button class="ac-btn-delete" *ngIf="isEditMode" (click)="requestDelete()" [disabled]="isSubmitting">
          <span>{{ 'common.delete' | translate }}</span>
          <mat-icon class="ac-btn-icon">delete_outline</mat-icon>
        </button>
        <button class="ac-btn-proceed" (click)="proceed()" [disabled]="isSubmitting">
          <span>{{ (isEditMode ? 'customers.add_contact.save' : 'customers.add_contact.create') | translate }}</span>
          <mat-icon class="ac-btn-icon">{{ isSubmitting ? 'hourglass_empty' : 'check' }}</mat-icon>
        </button>
      </ng-container>
    </div>
  </div>
</div>
